import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useThrottle } from './useThrottle';

const LAYOUT_DISPLAYS = new Set(['grid', 'inline-grid', 'table', 'table-row', 'table-cell', 'inline-table', 'contents']);

/**
 * Walks up the DOM from a starting element to find the nearest ancestor whose
 * width is determined by layout (grid track, table column) rather than content.
 * Returns `null` if no such ancestor is found.
 */
export const findLayoutConstrainedAncestor = (startElement: HTMLElement | null): HTMLElement | null => {
  if (!startElement) return null;
  if (startElement.offsetWidth > 0) {
    if (startElement.tagName === 'TD' || startElement.tagName === 'TH') return startElement;
    const { parentElement } = startElement;
    if (parentElement && LAYOUT_DISPLAYS.has(getComputedStyle(parentElement).display)) return startElement;
  }
  return findLayoutConstrainedAncestor(startElement.parentElement);
};

interface UseDynamicLimitProps {
  fixedLimit?: number;
  items: any[];
  reservedSpaceForLastElement?: number;
  horizontalGapBetweenItems?: number;
  overlapBetweenItems?: number;
  additionalPaddingPerItem?: number;
  parentElementRef?: React.RefObject<HTMLElement | null>;
  /**
   * Lazily resolves the parent element for measurement. Called once after mount.
   * Use this when the parent element isn't available as a ref but can be found
   * by walking the DOM (e.g., finding the nearest grid/table cell ancestor).
   * Takes precedence over parentElementRef when both are provided.
   */
  resolveParent?: () => HTMLElement | null;
}

export const useDynamicVisibleItemsLimit = (
  containerRef: React.RefObject<HTMLElement | null>,
  {
    fixedLimit,
    items,
    reservedSpaceForLastElement = 30,
    horizontalGapBetweenItems = 16,
    overlapBetweenItems,
    additionalPaddingPerItem = 0,
    parentElementRef,
    resolveParent,
  }: UseDynamicLimitProps
) => {
  const [visibleItemsLimit, setVisibleItemsLimit] = useState(items.length);
  const cachedItemWidths = useRef<number[]>([]);
  const hasMeasured = useRef(false);
  const resolvedParentCache = useRef<HTMLElement | null>(null);

  const getParentElement = useCallback(() => {
    if (parentElementRef?.current) return parentElementRef.current;
    if (resolveParent) {
      if (!resolvedParentCache.current) {
        resolvedParentCache.current = resolveParent();
      }
      return resolvedParentCache.current;
    }
    return null;
  }, [parentElementRef, resolveParent]);

  const measureAndCacheWidths = useCallback(() => {
    if (!containerRef.current) return false;
    const children = Array.from(containerRef.current.children) as HTMLElement[];
    // Only cache when all items are rendered (before any truncation)
    if (children.length < items.length) return cachedItemWidths.current.length > 0;

    cachedItemWidths.current = children.slice(0, items.length).map(child => child.offsetWidth + additionalPaddingPerItem);
    return true;
  }, [containerRef, items.length, additionalPaddingPerItem]);

  const calculateVisibleItemsLimit = useCallback(() => {
    const parent = getParentElement();
    if (!parent || !containerRef.current) return;

    if (cachedItemWidths.current.length < items.length) {
      if (!measureAndCacheWidths()) return;
    }

    const parentElementWidth = parent.offsetWidth;
    const spaceAvailableForItems = items.length > 1 ? parentElementWidth - reservedSpaceForLastElement : parentElementWidth;

    const { countOfVisibleElements } = cachedItemWidths.current.reduce(
      (acc, widthOfCurrentItem, index) => {
        if (acc.limitReached) return acc;

        const widthAfterAddingCurrentItem = overlapBetweenItems
          ? index === 0
            ? widthOfCurrentItem
            : acc.currentTotalWidth + widthOfCurrentItem - overlapBetweenItems
          : acc.currentTotalWidth + widthOfCurrentItem + (index > 0 ? horizontalGapBetweenItems : 0);

        return widthAfterAddingCurrentItem <= spaceAvailableForItems
          ? { currentTotalWidth: widthAfterAddingCurrentItem, countOfVisibleElements: acc.countOfVisibleElements + 1, limitReached: false }
          : { ...acc, limitReached: true };
      },
      { currentTotalWidth: 0, countOfVisibleElements: 0, limitReached: false }
    );

    setVisibleItemsLimit(countOfVisibleElements);
  }, [
    containerRef,
    getParentElement,
    measureAndCacheWidths,
    reservedSpaceForLastElement,
    horizontalGapBetweenItems,
    overlapBetweenItems,
    items.length,
  ]);

  const scheduleMeasure = useThrottle(calculateVisibleItemsLimit, 100);

  // Initial measurement — runs synchronously before paint to avoid layout shift.
  // Only runs when parentElementRef is provided (reliable at useLayoutEffect time).
  // When resolveParent is used, we defer to useEffect since the DOM walk needs
  // fully-applied CSS-in-JS styles for accurate width measurement.
  useLayoutEffect(() => {
    if (fixedLimit !== undefined || hasMeasured.current || !parentElementRef) return;

    const parent = getParentElement();
    if (!parent) return;

    measureAndCacheWidths();
    calculateVisibleItemsLimit();
    hasMeasured.current = true;
  });

  // Resize observation + deferred initial measurement for resolveParent
  useEffect(() => {
    if (fixedLimit !== undefined) return;

    const parent = getParentElement();
    if (!parent) return;

    // Ensure initial measurement happened (covers resolveParent and late-mount cases)
    if (!hasMeasured.current) {
      measureAndCacheWidths();
      calculateVisibleItemsLimit();
      hasMeasured.current = true;
    }

    const resizeObserver = new ResizeObserver(() => requestAnimationFrame(scheduleMeasure));
    resizeObserver.observe(parent);

    window.addEventListener('resize', scheduleMeasure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [items.length, fixedLimit, getParentElement, calculateVisibleItemsLimit, measureAndCacheWidths, scheduleMeasure]);

  // Invalidate cached widths and resolved parent when items change
  const prevItemsLength = useRef(items.length);
  useEffect(() => {
    if (prevItemsLength.current !== items.length) {
      prevItemsLength.current = items.length;
      cachedItemWidths.current = [];
      resolvedParentCache.current = null;
      hasMeasured.current = false;
      setVisibleItemsLimit(items.length);
    }
  }, [items.length]);

  return fixedLimit ?? visibleItemsLimit;
};
